import { useMemo, useState } from 'react';
import { Button, Empty, Input, Select, Tag } from 'antd';
import { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { useConsoleStore, type ConsoleLevel } from '@presentation/stores/consoleStore';

const LEVEL_COLORS: Record<ConsoleLevel, string> = {
  log: 'default',
  info: 'blue',
  warn: 'gold',
  error: 'red',
};

const LEVEL_OPTIONS: { label: string; value: ConsoleLevel | 'all' }[] = [
  { label: 'All levels', value: 'all' },
  { label: 'Log', value: 'log' },
  { label: 'Info', value: 'info' },
  { label: 'Warn', value: 'warn' },
  { label: 'Error', value: 'error' },
];

interface ConsolePanelProps {
  onClose: () => void;
}

export const ConsolePanel = ({ onClose }: ConsolePanelProps) => {
  const entries = useConsoleStore((state) => state.entries);
  const clear = useConsoleStore((state) => state.clear);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<ConsoleLevel | 'all'>('all');

  const filtered = useMemo(
    () =>
      entries
        .filter((entry) => levelFilter === 'all' || entry.level === levelFilter)
        .filter(
          (entry) =>
            !search.trim() || entry.message.toLowerCase().includes(search.trim().toLowerCase()),
        )
        .slice()
        .reverse(),
    [entries, levelFilter, search],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-black/10 px-2 py-1.5 dark:border-white/10">
        <span className="text-xs font-medium text-gray-500">Console</span>
        <Input.Search
          size="small"
          placeholder="Filter console"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          allowClear
          className="w-52"
        />
        <Select
          size="small"
          value={levelFilter}
          onChange={setLevelFilter}
          options={LEVEL_OPTIONS}
          className="w-32"
        />
        <div className="flex-1" />
        <Button size="small" icon={<DeleteOutlined />} onClick={clear}>
          Clear
        </Button>
        <Button
          size="small"
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label="Close console"
        />
      </div>

      <div className="flex-1 overflow-auto font-mono text-xs">
        {filtered.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <Empty description="Nothing logged yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          filtered.map((entry) => (
            <div
              key={entry.id}
              className="flex items-start gap-2 border-b border-black/5 px-2 py-1 dark:border-white/5"
            >
              <span className="shrink-0 text-gray-400">
                {new Date(entry.timestamp).toLocaleTimeString()}
              </span>
              <Tag color={LEVEL_COLORS[entry.level]} className="shrink-0">
                {entry.source}
              </Tag>
              <span className="min-w-0 flex-1 whitespace-pre-wrap break-all">{entry.message}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
